/**
 * Aceternity UI — Resizable Navbar (adapted for Vite + framer-motion + TanStack Link)
 * https://ui.aceternity.com/components/resizable-navbar
 */
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { AppLink } from '../shared/AppLink';

type NavbarProps = {
  children: React.ReactNode;
  className?: string;
  /** Viewport Y of the sticky header’s bottom edge (clears safe-area + scroll shift). */
  onBottomChange?: (bottomPx: number) => void;
};

type NavBodyProps = {
  children: React.ReactNode;
  className?: string;
  visible?: boolean;
};

type NavItemsProps = {
  items: { name: string; link: string }[];
  className?: string;
  onItemClick?: () => void;
};

type MobileNavProps = {
  children: React.ReactNode;
  className?: string;
  visible?: boolean;
};

type MobileNavHeaderProps = {
  children: React.ReactNode;
  className?: string;
};

type MobileNavMenuProps = {
  children: React.ReactNode;
  className?: string;
  isOpen: boolean;
  onClose: () => void;
};

export const Navbar = ({ children, className, onBottomChange }: NavbarProps) => {
  const { scrollY } = useScroll();
  const [visible, setVisible] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setVisible(latest > 80);
  });

  useEffect(() => {
    const el = rootRef.current;
    if (!el || !onBottomChange) return;

    const report = () => {
      // Prefer the visible chrome child so `lg:hidden` / `hidden lg:flex` don’t report a 0-height box.
      const visibleChrome = Array.from(el.children).find((child) => {
        const node = child as HTMLElement;
        return node.offsetParent !== null || node.getClientRects().length > 0;
      }) as HTMLElement | undefined;
      const bottom = Math.ceil((visibleChrome ?? el).getBoundingClientRect().bottom);
      if (bottom > 0) onBottomChange(bottom);
    };

    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    window.addEventListener('scroll', report, { passive: true });
    window.addEventListener('resize', report);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', report);
      window.removeEventListener('resize', report);
    };
  }, [onBottomChange, visible]);

  return (
    <div
      ref={rootRef}
      className={cn('pointer-events-none fixed inset-x-0 top-0 z-[9999] w-full', className)}
      style={{
        top: 'var(--safe-area-top, env(safe-area-inset-top, 0px))',
      }}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child as React.ReactElement<{ visible?: boolean }>, { visible })
          : child
      )}
    </div>
  );
};

/** Gentle scroll chrome — keep nav readable; avoid abrupt shrink. */
const NAV_SCROLL_TRANSITION = {
  type: 'tween' as const,
  duration: 0.45,
  ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
};

export const NavBody = ({ children, className, visible }: NavBodyProps) => {
  /** Stacked brand + nav uses `flex-col` via className — keep wide enough for link row. */
  const stacked = typeof className === 'string' && className.includes('nav-body-stacked');

  return (
    <motion.div
      animate={{
        backdropFilter: visible ? 'blur(12px)' : 'blur(0px)',
        boxShadow: visible
          ? '0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset'
          : '0 0 0 rgba(0,0,0,0)',
        width: visible ? (stacked ? 'min(960px, 96vw)' : 'min(920px, 92vw)') : '100%',
        y: visible ? 10 : 0,
      }}
      transition={NAV_SCROLL_TRANSITION}
      style={{ minWidth: visible ? (stacked ? 'min(640px, 94vw)' : 'min(720px, 92vw)') : '100%' }}
      className={cn(
        'pointer-events-auto relative z-[60] mx-auto hidden w-full max-w-7xl flex-row items-center justify-between self-start rounded-full bg-transparent px-4 py-2 lg:flex',
        visible && 'bg-white/85',
        className
      )}
    >
      {children}
    </motion.div>
  );
};

export const NavItems = ({ items, className, onItemClick }: NavItemsProps) => {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <motion.div
      onMouseLeave={() => setHovered(null)}
      className={cn(
        'absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-2 text-sm font-medium text-zinc-600 transition duration-200 hover:text-zinc-800 lg:flex lg:space-x-2',
        className
      )}
    >
      {items.map((item, idx) => (
        <AppLink
          onMouseEnter={() => setHovered(idx)}
          onClick={onItemClick}
          className="relative px-4 py-2 text-neutral-700"
          key={`link-${idx}`}
          to={item.link}
        >
          {hovered === idx && (
            <motion.div
              layoutId="hovered"
              className="absolute inset-0 h-full w-full rounded-full bg-gray-100"
            />
          )}
          <span className="relative z-20">{item.name}</span>
        </AppLink>
      ))}
    </motion.div>
  );
};

export const MobileNav = ({ children, className, visible }: MobileNavProps) => {
  return (
    <motion.div
      animate={{
        backdropFilter: visible ? 'blur(12px)' : 'blur(0px)',
        boxShadow: visible
          ? '0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset'
          : '0 0 0 rgba(0,0,0,0)',
        width: visible ? '96%' : '100%',
        paddingRight: visible ? '8px' : '0px',
        paddingLeft: visible ? '8px' : '0px',
        borderRadius: visible ? '1rem' : '1.25rem',
        // Small shift only — large `y` collides with sticky page toolbars.
        y: visible ? 4 : 0,
      }}
      transition={NAV_SCROLL_TRANSITION}
      className={cn(
        'pointer-events-auto relative z-50 mx-auto flex w-full max-w-[calc(100vw-0.75rem)] flex-col items-center justify-center overflow-hidden bg-transparent px-0 py-1.5 lg:hidden',
        visible && 'bg-white/85',
        className
      )}
    >
      {children}
    </motion.div>
  );
};

export const MobileNavHeader = ({ children, className }: MobileNavHeaderProps) => {
  return (
    <div className={cn('flex w-full flex-row items-center justify-between', className)}>
      {children}
    </div>
  );
};

export const MobileNavMenu = ({
  children,
  className,
  isOpen,
  onClose,
}: MobileNavMenuProps) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={cn(
            'absolute inset-x-0 top-16 z-50 flex w-full flex-col items-start justify-start gap-4 rounded-lg bg-white px-4 py-8 shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset]',
            className
          )}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const MobileNavToggle = ({
  isOpen,
  onClick,
}: {
  isOpen: boolean;
  onClick: () => void;
}) => {
  return isOpen ? (
    <button type="button" onClick={onClick} aria-label="Close menu" className="text-black">
      <X className="h-6 w-6" />
    </button>
  ) : (
    <button type="button" onClick={onClick} aria-label="Open menu" className="text-black">
      <Menu className="h-6 w-6" />
    </button>
  );
};

export const NavbarLogo = ({
  title = 'Pignon Rouge',
  subtitle,
  to = '/',
  logoSrc = '/images/PPRLOGO.PNG',
  logoAlt = 'Pourvoirie Pignon Rouge',
  compact = false,
}: {
  title?: string;
  subtitle?: string;
  to?: string;
  logoSrc?: string;
  logoAlt?: string;
  /** Hide brand text on very small bars (image logo still shown). */
  compact?: boolean;
}) => {
  return (
    <AppLink
      to={to}
      className="relative z-20 mr-2 flex min-w-0 items-center gap-2 px-1 py-1 text-sm font-normal text-black md:mr-4 md:gap-3 md:px-2"
      aria-label="Go to Home"
    >
      <img
        id="logo"
        src={logoSrc}
        alt={logoAlt}
        className="h-11 w-11 shrink-0 object-contain sm:h-12 sm:w-12 md:h-[4.25rem] md:w-[4.25rem] lg:h-20 lg:w-20"
      />
      {!compact ? (
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="font-felix-titling truncate text-sm font-semibold uppercase tracking-tight text-[#3d2817] sm:text-base md:text-lg lg:text-xl">
            {title}
          </span>
          {subtitle ? (
            <span className="mt-0.5 truncate text-[10px] uppercase tracking-wide text-[#3d2817]/75 sm:text-[11px] md:text-xs lg:text-[13px]">
              {subtitle}
            </span>
          ) : null}
        </div>
      ) : null}
    </AppLink>
  );
};

export const NavbarButton = ({
  href,
  as: Tag = 'a',
  children,
  className,
  variant = 'primary',
  ...props
}: {
  href?: string;
  as?: React.ElementType;
  children: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary' | 'dark' | 'gradient';
} & React.ComponentPropsWithoutRef<'a' | 'button'>) => {
  const baseStyles =
    'px-4 py-2 rounded-md text-sm font-bold relative cursor-pointer hover:-translate-y-0.5 transition duration-200 inline-block text-center';

  const variantStyles = {
    primary:
      'bg-[#2f9e44] text-white shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset]',
    secondary: 'bg-transparent text-black shadow-none',
    dark: 'bg-black text-white shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset]',
    gradient:
      'bg-gradient-to-b from-blue-500 to-blue-700 text-white shadow-[0px_2px_0px_0px_rgba(255,255,255,0.3)_inset]',
  };

  if (href && (Tag === 'a' || Tag === AppLink)) {
    return (
      <AppLink
        to={href}
        className={cn(baseStyles, variantStyles[variant], className)}
      >
        {children}
      </AppLink>
    );
  }

  return (
    <Tag
      className={cn(baseStyles, variantStyles[variant], className)}
      {...props}
    >
      {children}
    </Tag>
  );
};
